import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Brain } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'

interface ThinkingBlockProps {
  thinking: string
  isStreaming?: boolean  // Show expanded while streaming, collapsed after
}

export function ThinkingBlock({ thinking, isStreaming = false }: ThinkingBlockProps) {
  // Start expanded when streaming, stay expanded until user collapses
  const [isExpanded, setIsExpanded] = useState(isStreaming)

  // Auto-expand when streaming starts
  useEffect(() => {
    if (isStreaming) {
      setIsExpanded(true)
    }
  }, [isStreaming])

  if (!thinking) return null

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.08em]',
          'hover:text-foreground transition-colors',
          isStreaming ? 'text-foreground/70' : 'text-muted-foreground'
        )}
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
        )}
        <Brain className={cn("h-3 w-3", isStreaming && "animate-pulse")} strokeWidth={1.5} />
        <span>Thinking</span>
        {isStreaming && (
          <span className="inline-block w-1.5 h-3 bg-foreground/50 animate-pulse ml-1" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 pl-3 border-l border-border/40">
          <div className={cn(
            // Base typography - scaled down, muted
            "text-[11px] leading-relaxed text-muted-foreground/80",
            // Paragraphs - tight spacing
            "[&>p]:mb-2 [&>p]:mt-0",
            // Headings - slightly emphasized
            "[&>h1]:text-xs [&>h1]:font-medium [&>h1]:text-muted-foreground [&>h1]:mb-1.5 [&>h1]:mt-3",
            "[&>h2]:text-[11px] [&>h2]:font-medium [&>h2]:text-muted-foreground [&>h2]:mb-1 [&>h2]:mt-2",
            "[&>h3]:text-[11px] [&>h3]:font-medium [&>h3]:text-muted-foreground [&>h3]:mb-1 [&>h3]:mt-2",
            // Lists - proper bullets, compact spacing
            "[&>ul]:my-1.5 [&>ul]:pl-4 [&>ul]:list-disc",
            "[&>ol]:my-1.5 [&>ol]:pl-4 [&>ol]:list-decimal",
            "[&_li]:my-0.5 [&_li]:leading-relaxed",
            "[&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-1",
            "[&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:my-1",
            // Emphasis
            "[&_strong]:font-medium [&_strong]:text-muted-foreground",
            "[&_em]:italic",
            // Code
            "[&_code]:text-[10px] [&_code]:bg-muted/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded",
            // Blockquotes
            "[&_blockquote]:border-l-2 [&_blockquote]:border-border/50 [&_blockquote]:pl-2 [&_blockquote]:italic [&_blockquote]:my-1.5"
          )}>
            <ReactMarkdown>{thinking}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  )
}
